/**
 * Workflow Progress API
 *
 * Centralized API client for polling workflow progress events.
 * Shared by all generation workflows (lesson, course, etc.).
 * The poll endpoint is workflow-agnostic — it works with any job_id.
 */

import { apiClient } from '@/core/config/api';

/** Progress event from the workflow engine (Pusher or poll) */
export interface WorkflowProgressEvent {
  type?: 'init' | 'progress' | 'done' | 'keepalive' | 'error';
  job_id?: string;
  sequence_number?: number;
  step_id?: string;
  status?: string;
  progress_percent?: number;
  message?: string;
  timestamp?: string;
  metadata?: Record<string, unknown>;
}

/** Response from the poll endpoint, including job-level status */
export interface WorkflowPollResponse {
  job_id: string;
  job_status: string;
  events: WorkflowProgressEvent[];
  suggested_poll_interval_ms?: number;
  source?: string;
}

/**
 * Poll for workflow progress events.
 * Reads from Redis Streams first, falls back to PostgreSQL.
 * Returns both events and job_status for failure detection.
 */
export async function pollWorkflowProgress(
  jobId: string,
  lastSequence = 0,
): Promise<WorkflowPollResponse> {
  const response = await apiClient.get(
    `/lesson-generation/poll/${jobId}?last_sequence=${lastSequence}`,
  );
  return response.data;
}
